import { msg } from '@lingui/core/macro';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useMatch } from 'react-router-dom';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

import { AppDispatch } from 'store/store';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { fetchBoardsByTemplateId } from 'modules/template-editor/store/templates/actions';
import { setActiveBoard, setActiveTemplate } from 'modules/template-editor/store/templates/templates';
import { useOwnerBoardTemplates } from './use-owner-board-templates';
import useTemplates from './use-templates';

/**
 * Данные страниц редактора табло: подписка на список шаблонов организации, доски шаблона из адреса и
 * активные шаблон и доска по адресу.
 *
 * Список сам хук не грузит — его держит кэш RTK Query (`useOwnerBoardTemplates`): все страницы и форма
 * турнира с теми же аргументами делят одну запись, и повторный рендер, смена объекта организации или
 * StrictMode второго запроса не шлют (IMGM-1566).
 */
export function useLoadTemplates() {
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const toastApiError = useToastApiError();
  const { templateId } = useMatch(Pages.TEMPLATE.BOARDS + '/*')?.params ?? {};
  const { boardId } = useMatch(Pages.TEMPLATE.EDITOR + '/*')?.params ?? {};

  const { error: templatesError } = useOwnerBoardTemplates();
  const { loadedTemplates } = useTemplates();

  // Тост — один на ошибку: объект ошибки в кэше меняется только с новым запросом.
  useEffect(() => {
    if (templatesError) toastApiError(templatesError, msg`Не удалось загрузить шаблоны`);
  }, [templatesError, toastApiError]);

  useEffect(() => {
    if (templateId && !loadedTemplates.includes(Number(templateId))) {
      dispatchWithToast(fetchBoardsByTemplateId({ templateId: Number(templateId) }), { error: msg`Не удалось загрузить табло` });
    }
  }, [dispatchWithToast, templateId, loadedTemplates]);

  useEffect(() => {
    if (templateId) {
      dispatch(setActiveTemplate({ id: parseInt(templateId) }));
    }

    if (boardId) {
      dispatch(setActiveBoard({ id: parseInt(boardId) }));
    }
  }, [boardId, dispatch, templateId]);
}
